import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PersonStanding } from 'lucide-react'
import * as Switch from '@radix-ui/react-switch'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'  // 409
import { userApi } from '@/api/endpoints'  // 409

// ============================================================================
// Display (#301) — per-browser choices for every user
// ============================================================================
// Item 413: the running-light switch is gone (only open Critical rows have a light, and it always runs), so this
// section holds the superuser pose default (409) and renders nothing for anyone else.

export function DisplaySection() {
  const { t } = useTranslation('settings')

  // 409 — superusers: the default for the "Show pose" switch on event videos, kept on the profile (absent = on).
  // The switch shows the saved value only; it changes once the server has accepted the change.
  const isSuperuser = useAuthStore((s) => s?.user?.is_superuser === true)
  const poseDefault = useAuthStore((s) => s?.user?.pose_overlay_default !== false)
  const updateUser = useAuthStore((s) => s.updateUser)
  const [poseSaving, setPoseSaving] = useState(false)
  const [poseError, setPoseError] = useState(false)
  const poseId = 'display-pose-default'
  const savePoseDefault = async (value: boolean) => {
    setPoseSaving(true)
    setPoseError(false)
    try {
      await userApi.patchProfile({ pose_overlay_default: value })
      updateUser({ pose_overlay_default: value })
    } catch {
      setPoseError(true)
    } finally {
      setPoseSaving(false)
    }
  }

  if (!isSuperuser) return null

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{t('display.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('display.description')}</p>
      </div>

      <div className="rounded-lg border bg-card p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-teal-600 dark:text-teal-400">
            <PersonStanding className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <label htmlFor={poseId} className="text-sm font-medium">{t('display.poseDefault.title')}</label>
            <p id={`${poseId}-hint`} className="text-xs text-muted-foreground">{t('display.poseDefault.hint')}</p>
          </div>
          <Switch.Root
            id={poseId}
            checked={poseDefault}
            disabled={poseSaving}
            onCheckedChange={savePoseDefault}
            aria-describedby={`${poseId}-hint`}
            className={cn(
              'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-wait disabled:opacity-60',
              'data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-muted-foreground/30',
            )}
          >
            <Switch.Thumb className="pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
          </Switch.Root>
        </div>
        {poseError && <p role="alert" className="mt-2 text-xs text-rose-600 dark:text-rose-400">{t('display.poseDefault.saveFailed')}</p>}
      </div>
    </section>
  )
}
